import { useCallback } from 'react';
import { type Href, useLocalSearchParams, useRouter } from 'expo-router';
import { Platform } from 'react-native';

import { useIsNavigationReady } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import {
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
} from '../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useMenuContentNavigation = (params: UseMenuContentNavigationParams) => {
  const { restaurantSlug, orderChannel, deliveryDetailsData } = params;

  const router = useRouter();
  const isNavigationReady = useIsNavigationReady();
  const routeParams = useLocalSearchParams<MaybeParamsWithCategorySlug>();
  const { trackEvent } = useTelemetry();

  const defaultActiveTargetId = routeParams.category;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const changeLocation = useCallback(() => {
    trackEvent({ name: 'menu.change_location' });

    router.navigate('/location-selection');
  }, [router, trackEvent]);

  const generateCustomizationHref = useCallback(
    (productSlug: string): Href => {
      if (orderChannel === 'delivery') {
        const addressId = deliveryDetailsData?.addressId ?? '';

        return {
          pathname: '/delivery/[addressId]/[productSlug]',
          params: { addressId, productSlug },
        };
      }

      return {
        pathname: '/[restaurantSlug]/[productSlug]',
        params: { restaurantSlug, productSlug },
      };
    },
    [deliveryDetailsData?.addressId, orderChannel, restaurantSlug],
  );

  /**
   * Keeps the active category in the URL so the menu stays shareable and
   * deep-linkable.
   *
   * The scrollspy calls this on every category boundary crossed, so the URL is
   * written directly rather than through `router.setParams`. `expo-router`
   * exposes route info as a single unselected snapshot, so a navigation state
   * change re-renders every `usePathname`, `useSegments`, and search param
   * consumer mounted in the app, along with the whole menu tree.
   *
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/History/replaceState}
   */
  const setTargetParam = useCallback(
    (targetSlug: string) => {
      if (Platform.OS !== 'web' || !isNavigationReady) return;

      const url = new URL(window.location.href);

      if (url.searchParams.get(CATEGORY_PARAM) === targetSlug) return;

      url.searchParams.set(CATEGORY_PARAM, targetSlug);

      // NOTE: The current history state is carried over so the router's own
      //       entry metadata survives the rewrite.
      window.history.replaceState(window.history.state, '', url);
    },
    [isNavigationReady],
  );

  const openContactUs = useCallback(() => {
    router.navigate('/chat');
  }, [router]);

  return {
    defaultActiveTargetId,
    changeLocation,
    generateCustomizationHref,
    setTargetParam,
    openContactUs,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORY_PARAM = 'category';

// ─── Types ───────────────────────────────────────────────────────────────────

type UseMenuContentNavigationParams = {
  restaurantSlug: string;
  deliveryDetailsData?: MenuContentDeliveryDetailsData;
  orderChannel: MenuContentOrderChannel;
};

type MaybeParamsWithCategorySlug = { category?: string };
